import styles from "./styles/want-more-info.module.scss";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/high-res.css";
import React, { FormEvent, useState } from "react";
import { activateReCAPTCHA, submitForm } from "@/components/forms/lib";
import { useTranslation, withTranslation } from "react-i18next";
import { IFormFields } from "@/components/forms/interfaces/ISignUpForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import ProductOfInterest from "@/components/forms/components/product-of-interest";
import classNames from "classnames";
import PrivacyText from "../privacy-text";

const WantMoreInfo: React.FC<{
  message?: boolean;
  className?: string;
  formId: string;
  formClassName?: string;
  translationPrefix?: string;
  productName?: string;
  leadSourceUTMCampaign?: string;
  hearXProductMulti?: string;
}> = ({
  message,
  className = "",
  formId,
  formClassName,
  translationPrefix,
  productName,
  leadSourceUTMCampaign,
  hearXProductMulti,
}) => {
  const { t } = useTranslation();

  const withTranslationPrefix = (string: string) =>
    translationPrefix ? translationPrefix + "." + string : string;
  const [leadID, setLeadID] = useState("");

  // ReCAPTCHA Variables
  const [reCAPTCHAActive, setReCAPTCHAActive] = useState(false);
  const [reCAPTCHAError, setReCAPTCHAError] = useState("");
  // Form variables
  const [formIsLoading, setFormIsLoading] = useState(false);
  const [formError, setFormError] = useState({
    error: false,
    field: "",
  } as { error: boolean; field?: string });
  const [thankYouState, setThankYouState] = useState({
    success: false,
    message: "",
  });

  // Form state
  const [formState, setFormState] = useState<IFormFields>({
    FIRST_NAME: "",
    LAST_NAME: "",
    EMAIL: "",
    LEAD_SOURCE_ID: 3292000,
    LEAD_DESCRIPTION: "",
    PHONE: "",
    ORGANISATION_NAME: "",
    CUSTOMFIELDS: [
      {
        FIELD_NAME: "Lead_Source_Secondary__c",
        FIELD_VALUE: "Website Product Enquiry",
      },
      {
        FIELD_NAME: "hearX_product_multi__c",
        FIELD_VALUE: hearXProductMulti || "Not Specified",
      },
      ...(leadSourceUTMCampaign
        ? [
            {
              FIELD_NAME: "Lead_Source_UTM_Campaign__c",
              FIELD_VALUE: leadSourceUTMCampaign || "",
            },
          ]
        : []),
      {
        FIELD_NAME: "Country_Code__c",
        FIELD_VALUE: "+1",
      },
    ],
  });

  /*
	Handler to submit the form data
	*/
  const handleSubmit = () => {
    // Setting the state of the form that it is busy processing the request
    setFormIsLoading(true);
    // Submit the request
    submitForm(
      formState,
      (response: {
        error: boolean;
        field?: string;
        responseData?: Record<string, any>;
      }) => {
        // Set the form error state
        setFormError(response);

        // Set the form back to ready to process
        setFormIsLoading(false);

        // Set the reCAPTCHA errors to nothing
        setReCAPTCHAError("");

        // Check if there are any errors in the response
        if (response.error) {
          // Set reCAPTCHA Errors for the user to see
          if (response.field) {
            switch (response.field) {
              case "RE_CAPTCHA_NOT_FOUND":
              case "RE_CAPTCHA_NULL":
                setReCAPTCHAError(
                  t(
                    withTranslationPrefix(
                      "forms.error-messages.re-captcha.empty"
                    )
                  )
                );
                break;
              case "RE_CAPTCHA":
                setReCAPTCHAError(
                  t(
                    withTranslationPrefix(
                      "forms.error-messages.re-captcha.invalid"
                    )
                  )
                );
                break;
            }
          }
          return;
        }

        response.responseData && setLeadID(response.responseData.data.LEAD_ID);

        // If the form was requested successfully set this to success === true
        setThankYouState({
          success: true,
          message: t(withTranslationPrefix("forms.success.get-in-touch")),
        });
      }
    );
  };

  /*
   Handler to update the form state when a field changes its value
   */
  const onChangeHandler = (
    event: FormEvent<HTMLInputElement> | FormEvent<HTMLTextAreaElement>
  ) => {
    const newFormState = { ...formState } as IFormFields;
    const fieldName = event.currentTarget.name;

    Object.keys(newFormState).forEach((key: string) => {
      let value: string = event.currentTarget.value;
      if (key === "LEAD_DESCRIPTION") {
        if (value.length > 250) {
          value = value.substring(0, 250);
        }
      }
      if (key === fieldName) {
        newFormState[key] = value;
      }
    });

    setFormState(newFormState);
  };

  /*
   Function that sets the product selected
   */
  const onProductSelect = (product: string) => {
    const newFormState = { ...formState } as IFormFields;

    newFormState.CUSTOMFIELDS[1].FIELD_VALUE = product;

    setFormState(newFormState);
  };

  /*
	Function that handles the phone number change
	*/
  const onPhoneNumberChange = (
    number: string,
    countryData: {
      name: string;
      dialCode: string;
      countryCode: string;
      format: string;
    }
  ) => {
    const newFormState = { ...formState } as IFormFields;

    newFormState.PHONE = `+${number}`;
    const countryCodeField = newFormState.CUSTOMFIELDS.find(
      (field) => field.FIELD_NAME === "Country_Code__c"
    );
    if (countryCodeField) {
      countryCodeField.FIELD_VALUE = `+${countryData.dialCode}`;
    }

    setFormState(newFormState);
  };

  /*
	Handler to warm up reCAPTCHA
	*/
  const handleReCAPTCHAActivation = () => {
    activateReCAPTCHA((active) => {
      setReCAPTCHAActive(active);
    });
  };

  return (
    <section className={classNames(className)}>
      <div className={styles.formWrapper}>
        <form
          id={formId}
          className={classNames(styles.inputWrapper, formClassName)}
          onClick={() => {
            if (!reCAPTCHAActive) {
              handleReCAPTCHAActivation();
            }
          }}
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <h2 className={styles.formHeading}>
            {t(withTranslationPrefix("forms.more-info.heading"))}
          </h2>
          <p className={styles.formSubHeading}>
            {t(withTranslationPrefix("forms.more-info.subheading"))}
          </p>
          <div className={styles.formRow}>
            <div>
              <input
                className={styles.formInputField}
                type="text"
                name="FIRST_NAME"
                placeholder={"First Name"}
                onInput={(e) => {
                  onChangeHandler(e);
                }}
                value={formState.FIRST_NAME}
                maxLength={26}
                required
              />
              {formError.error && formError.field === "FIRST_NAME" && (
                <p className={styles.errorMessage}>
                  {t(
                    withTranslationPrefix(
                      "forms.error-messages.first-name.invalid"
                    )
                  )}
                </p>
              )}
            </div>
            <div>
              <input
                className={styles.formInputField}
                type="text"
                name="LAST_NAME"
                placeholder={"Last Name"}
                onInput={(e) => {
                  onChangeHandler(e);
                }}
                maxLength={26}
                value={formState.LAST_NAME}
                required
              />
              {formError.error && formError.field === "LAST_NAME" && (
                <p className={styles.errorMessage}>
                  {t(
                    withTranslationPrefix(
                      "forms.error-messages.last-name.invalid"
                    )
                  )}
                </p>
              )}
            </div>
          </div>
          <div>
            <input
              className={styles.formInputField}
              type="text"
              name="EMAIL"
              placeholder={"Email"}
              onInput={(e) => {
                onChangeHandler(e);
              }}
              value={formState.EMAIL}
              required
            />
            {formError.error && formError.field === "EMAIL" && (
              <p className={styles.errorMessage}>
                {t(
                  withTranslationPrefix(
                    withTranslationPrefix("forms.error-messages.email.invalid")
                  )
                )}
              </p>
            )}
          </div>
          <div>
            <div
              className={classNames(styles.phoneInput, styles.formPhoneInput)}
            >
              <PhoneInput
                country={"us"}
                enableSearch={true}
                preferredCountries={["us", "za", "ca", "au", "gb"]}
                disableSearchIcon={true}
                inputClass={styles.inputClass}
                buttonClass={classNames(
                  styles.buttonClass,
                  styles.formPhoneButton
                )}
                placeholder={""}
                onChange={onPhoneNumberChange}
                value={formState.PHONE}
                inputProps={{ required: true }}
                countryCodeEditable={false}
                masks={{ za: ".. ... ...." }}
              />
            </div>
            {/* When invalid phone number is detected, show invalid */}
            {formError.error && formError.field === "PHONE" && (
              <p className={styles.errorMessage}>
                {t(withTranslationPrefix("forms.error-messages.phone.invalid"))}
              </p>
            )}
          </div>
          <div className={styles.formTextArea}>
            {message && (
              <div>
                <textarea
                  name="LEAD_DESCRIPTION"
                  className={styles.formInputTextField}
                  placeholder={t(
                    withTranslationPrefix(
                      "forms.want-more-info.placeholders.description"
                    ),
                    {
                      default: `Hi, I would like to know more${
                        productName && "about "
                      }${productName}.`,
                    }
                  )}
                  onInput={(e) => {
                    onChangeHandler(e);
                  }}
                  maxLength={250}
                  value={formState.LEAD_DESCRIPTION}
                />
                {formError.error && formError.field === "LEAD_DESCRIPTION" && (
                  <p className={styles.errorMessage}>
                    {t(
                      withTranslationPrefix(
                        "forms.error-messages.description.invalid"
                      )
                    )}
                  </p>
                )}
              </div>
            )}
          </div>
          <div>
            {/* Check if the form request was successful and display the correct component */}
            {thankYouState.success && thankYouState.message.length > 0 ? (
              <>
                <p className={styles.successMessage}>{thankYouState.message}</p>
                {leadID && (
                  <img
                    src={`https://www.shareasale.com/sale.cfm?tracking=${leadID}&amount=0.00&merchantID=142065&transtype=lead`}
                    width="1"
                    height="1"
                  />
                )}
              </>
            ) : (
              <button className={styles.formButton} type={"submit"}>
                {formIsLoading ? (
                  <FontAwesomeIcon
                    icon={faSpinner as IconProp}
                    spin={true}
                    className={styles.spinner}
                  />
                ) : (
                  t(withTranslationPrefix("forms.more-info.button"))
                )}
              </button>
            )}
            {/* ReCAPTCHA errors goes here */}
            {reCAPTCHAError !== "" && (
              <p className={styles.errorMessage}>{reCAPTCHAError}</p>
            )}

            {/* Request errors goes here */}
            {formError.error && formError.field === "REQUEST_ERROR" && (
              <p className={styles.errorMessage}>
                {t(
                  withTranslationPrefix("forms.error-messages.request.invalid")
                )}
              </p>
            )}
          </div>
          <div className={styles.privacyText}>
            <PrivacyText
              themeColor={{
                color: "!text-[#04a9f4]",
              }}
            />
          </div>
        </form>
      </div>
    </section>
  );
};

export default WantMoreInfo;
